import React, { useEffect } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CardActionArea from '@mui/material/CardActionArea';
import CardActions from '@mui/material/CardActions';
import blogs from '../../../../assets/json/blog-data.json'
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';

const FluidSection = () => {

  const { id } = useParams<{ id: string }>(); // Explicitly type id as string

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]); // Runs whenever id changes

  if (!id) return <p>Invalid blog ID</p>; // Handle undefined id

  const blog = blogs.find((b) => b.id === parseInt(id));

  if (!blog) return <p>Blog not found</p>;
  return (

    <div className="max-w-7xl mx-auto px-4">
      <div key={blog.id}>
        <div className="px-4">
          <div className="flex flex-row justify-between text-gray-600 text-sm py-2   mt-11 mb-11">
            <p className="text-[#979797]">{blog.date}</p>
            <p>{blog.category}</p>
            <p>{blog.readingTime}</p>
          </div>
          <div className="border-b border-gray-300" />

          <h1 className="text-[#3E3E3E] text-[20px] font-Helvetica Neue mt-14">{blog.title}</h1>
          {Array.isArray(blog.introduction) ? (
            blog.introduction.map((para, idx) => (
              <p key={idx} className="text-[18px] text-[#3E3E3E] leading-relaxed mt-4">
                {para}
              </p>
            ))
          ) : (
            <p className="text-[18px] text-[#3E3E3E] leading-relaxed mt-4">
              {blog.introduction}
            </p>
          )}
          {/* Core Section */}
          {blog.coreSection && (
            <>
              {/* Iterate over the new sections */}
              {["symptoms", "causes", "benefits", "solutions"].map((sectionKey) => {
                const sectionContent = (blog.coreSection as Record<string, any>)[sectionKey];

                if (!sectionContent) return null;

                return (
                  <div key={sectionKey} className="mt-10">
                    <h2 className="text-[#3E3E3E] font-bold text-[20px]">{sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1)}</h2>

                    {/* Render list items */}
                    {Array.isArray(sectionContent) && (
                      <ul className="text-[#3E3E3E] mt-4 text-[18px] leading-relaxed space-y-4 pl-6 custom-tick-list">
                        {sectionContent.map((item: string, i: number) => {
                          const [heading, content] = item.split(" – ");
                          return (
                            <li key={i}>
                              <span className="mr-2">★</span>
                              <span className="font-semibold">{heading}</span>
                              <br />
                              {content}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })}

              {/* Iterate over the remaining sections dynamically */}
              {Object.entries(blog.coreSection).map(([sectionTitle, sectionContent], idx) => {
                if (["symptoms", "causes", "benefits", "solutions"].includes(sectionTitle)) return null; // Skip if already processed

                if (!sectionContent) return null;

                return (
                  <div key={idx} className="mt-10">
                    <h2 className="text-[#3E3E3E] font-bold text-[20px]">{sectionTitle}</h2>

                    {/* Render paragraph content */}
                    {typeof sectionContent === "object" && sectionContent.content && !sectionContent.content.includes('|') && (
                      <p className="text-[#3E3E3E] mt-4 text-[18px] leading-relaxed">
                        {sectionContent.content}
                      </p>
                    )}

                    {/* Render list items */}
                    {sectionContent.items && Array.isArray(sectionContent.items) && (
                      <ul className="text-[#3E3E3E] mt-4 text-[18px] leading-relaxed space-y-4 pl-6 custom-tick-list">
                        {sectionContent.items.map((item: string, i: number) => {
                          const [heading, content] = item.split(" – ");
                          return (
                            <li key={i}>
                              <span className="mr-2">★</span>
                              <span className="font-semibold">{heading}</span>
                              <br />
                              {content}
                            </li>
                          );
                        })}
                      </ul>
                    )}

                    {/* Render table */}
                    {typeof sectionContent === "object" &&
                      sectionContent.content &&
                      sectionContent.content.includes("|") && (
                        <table className="w-full mt-6 border border-gray-300 text-left text-[#3E3E3E] text-[16px]">
                          <thead>
                            <tr className="bg-gray-100">
                              {sectionContent.content.split("\n")[0].split("|").map((head: string, i: number) => (
                                <th key={i} className="p-2 border border-gray-300 font-semibold">
                                  {head.trim()}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {sectionContent.content
                              .split("\n")
                              .slice(1)
                              .map((row: string, i: number) => (
                                <tr key={i}>
                                  {row.split("|").map((cell: string, j: number) => (
                                    <td key={j} className="p-2 border border-gray-300">
                                      {cell.trim()}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      )}
                  </div>
                );
              })}
            </>
          )}
          {blog.parkersec && (
            <>
              <h2 className="text-[#3E3E3E] font-bold text-[20px] mt-8">
                🔄 Easily Replace Competitor Valves with Parker Equivalents
              </h2>
              {Array.isArray(blog.parkersec) ? (
                blog.parkersec.map((para, idx) => (
                  <p key={idx} className="text-[18px] text-[#3E3E3E] leading-relaxed mt-4">
                    {para}
                  </p>
                ))
              ) : (
                <p className="text-[18px] text-[#3E3E3E] leading-relaxed mt-4">{blog.parkersec}</p>
              )}
            </>
          )}

          {blog.industries && (
            <div className="mt-10">
              <h2 className="text-[#3E3E3E] font-bold text-[20px]">
                {blog.industries.heading}
              </h2>
              <ul className="list-disc pl-5 mt-4 text-[#3E3E3E] text-[18px] leading-relaxed">
                {blog.industries.items.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
              <p className="text-[#3E3E3E] mt-4 text-[18px] leading-relaxed">
                {blog.industries.note}
              </p>
            </div>
          )}


          {/* Tips Section */}
          {blog.tips && (
            <>
              <h2 className="text-[#3E3E3E] font-bold text-[20px] mt-8">💬 Tips</h2>
              <ul className="text-[#3E3E3E] mt-8 text-[18px] leading-relaxed space-y-6 pl-6 custom-tick-list">
                {blog.tips.map((tip, index) => {
                  const [heading, content] = tip.split(' – ');
                  return (
                    <li key={index}>
                      <span className="mr-2">★</span>
                      <span className="font-semibold">{heading}</span>
                      <br />
                      {content}
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {blog.reason && (
            <div className="mt-10">
              <h2 className="text-[#3E3E3E] font-bold text-[20px]">
                {blog.reason.heading}
              </h2>
              <p className="text-[#3E3E3E] mt-4 text-[18px] leading-relaxed">
                {blog.reason.content}
              </p>
            </div>
          )}

          {typeof blog.callToAction === "object" && blog.callToAction.heading && (
            <h2 className="text-[#3E3E3E] font-bold text-[20px] mt-8">
              {blog.callToAction.heading}
            </h2>
          )}

          <p className="text-[#3E3E3E]  mt-4 text-[18px] leading-relaxed">
            {typeof blog.callToAction === "object" ? blog.callToAction.content : blog.callToAction}
          </p>
          {blog.notes && <p className="text-[#3E3E3E] mt-8 text-[18px] leading-relaxed">{blog.notes}</p>}
        </div>
        {/* Related Blogs Section */}
        {/* <div className="flex flex-row justify-between items-center text-gray-600 text-sm py-2 px-4 mt-14 mb-5">
          <p className="text-[20px] md:text-[35px] font-bold text-black mb-2 md:mb-0">Related Blogs</p>
          <p className="text-[18px] md:text-[22px] px-0 md:px-10 cursor-pointer">See More</p>
          <Link to="/about/blog">
           <p
                 onClick={() => window.scrollTo(0, 0)}
          className="text-[18px] md:text-[22px] px-0 md:px-10 cursor-pointer hover:text-blue-600">See More</p>
           </Link>
        </div> */}
         <div className="flex flex-row justify-between items-center text-gray-600 text-sm py-2 px-4 mt-14 mb-5">
          <p className="text-[20px] md:text-[35px] font-bold text-black mb-2 md:mb-0">Related Blogs</p>
          <Link to="/about/blog">
          <p
                onClick={() => window.scrollTo(0, 0)}
         className="text-[18px] md:text-[22px] px-0 md:px-10 cursor-pointer hover:text-blue-600">See More</p>
          </Link>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {blogs.filter((b) => b.id !== blog.id).slice(0, 3).map((relatedBlog) => (
              <Card key={relatedBlog.id} sx={{ maxWidth: 345 }}>
                <CardActionArea>
                  <Link to={`/about/blog/fluid-connector/${relatedBlog.id}`}>
                    <CardMedia
                      component="img"
                      height="140"
                      image={relatedBlog.imagePath}
                      alt={relatedBlog.title}
                      className="transition-transform duration-300 transform scale-110 hover:scale-100"
                    />

                    <CardContent className="flex flex-col justify-between text-left p-4 gap-4 md:gap-6 lg:gap-8 min-h-[220px] md:min-h-[250px] lg:min-h-[250px]">
                      <div className="flex-1 flex items-start mt-6">
                        <p className="text-black text-[18px] md:text-[19px] lg:text-[20px] font-medium font-helvetica leading-tight">
                          {relatedBlog.title}
                        </p>
                      </div>
                      <div className="flex flex-col mb-2">
                        <p className="text-[#979797] text-md">{relatedBlog.read}</p>
                        <p className="text-black text-base font-medium font-helvetica">{relatedBlog.category}</p>
                      </div>
                    </CardContent>
                  </Link>
                </CardActionArea>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


export default FluidSection;
